/**
 * ApprovalCardSideEffects component.
 *
 * Renders `card.side_effects` as a plain bullet list under a labeled section.
 * Strings are pre-substituted by the backend.
 */

import type { CSSProperties } from 'react'
import { AlertCircle } from 'lucide-react'

export interface ApprovalCardSideEffectsProps {
  /** List of side effect descriptions. */
  effects: string[]
}

export function ApprovalCardSideEffects({ effects }: ApprovalCardSideEffectsProps) {
  if (!effects || effects.length === 0) {
    return null
  }

  return (
    <section
      style={styles.container}
      aria-labelledby="side-effects-heading"
    >
      <div style={styles.header}>
        <AlertCircle
          style={styles.icon}
          aria-hidden="true"
        />
        <h3
          id="side-effects-heading"
          style={styles.heading}
        >
          Side Effects
        </h3>
      </div>
      <ul style={styles.list}>
        {effects.map((effect, index) => (
          <li key={index} style={styles.listItem}>{effect}</li>
        ))}
      </ul>
    </section>
  )
}

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(245, 158, 11, 0.3)',
    background: 'rgba(245, 158, 11, 0.05)',
    padding: '12px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  icon: {
    width: '16px',
    height: '16px',
    color: 'var(--warning)',
  },
  heading: {
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--text-primary)',
  },
  list: {
    marginLeft: '24px',
    listStyleType: 'disc',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: '14px',
    color: 'var(--text-primary)',
  },
  listItem: {
    paddingLeft: '4px',
  },
}

export default ApprovalCardSideEffects
